import { EyeIcon, EyeSlashIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { randomColor } from "@/components/properties/SelectEditor";
import { ReorderList } from "@/components/ReorderList";
import { AddRow, ColorDot, InlineName, Section } from "@/components/SettingsParts";
import { Button, Field, Tag } from "@/components/ui";
import { useFeedback } from "@/components/ui/feedback";
import { api } from "@/lib/api";
import { useProject } from "@/lib/project";
import type { Finance, FinanceCurrency } from "@/lib/types";
import { cn, moneyInput, parseMoney } from "@/lib/utils";
import { MoneyInput, MultiplierInput, useFinanceRun } from "./common";

function ContractSettings({ fin }: { fin: Finance }) {
  const { pid } = useProject();
  const run = useFinanceRun();
  const { toast } = useFeedback();
  const [amount, setAmount] = useState(fin.settings.agreedAmount ? moneyInput(fin.settings.agreedAmount) : "");
  const [currency, setCurrency] = useState(fin.settings.currency);
  const cents = amount.trim() === "" ? 0 : parseMoney(amount);
  const code = currency.trim().toUpperCase();
  const valid = cents !== null && /^[A-Z]{3}$/.test(code);
  const dirty = cents !== fin.settings.agreedAmount || code !== fin.settings.currency;

  return (
    <Section title="Contract" description="The total the client agreed to pay. Every amount is entered and calculated in the main currency.">
      <form
        className="flex max-w-lg flex-wrap items-start gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!valid || cents === null) return;
          if (await run(api.updateFinance(pid, { agreedAmount: cents, currency: code }))) toast("Contract saved", "success");
        }}
      >
        <div className="min-w-48 flex-1">
          <Field label="Agreed total" error={cents === null ? "Enter an amount like 25000 or 25000.50" : undefined}>
            <MoneyInput value={amount} onChange={setAmount} currency={code || "—"} invalid={cents === null} />
          </Field>
        </div>
        <div className="w-28">
          <Field label="Main currency" error={code && !/^[A-Z]{3}$/.test(code) ? "3 letters" : undefined}>
            <input
              value={currency}
              maxLength={3}
              onChange={(e) => setCurrency(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
              placeholder="USD"
              className="field font-mono uppercase"
              dir="ltr"
            />
          </Field>
        </div>
        {/* offset by the field label so the button lines up with the inputs */}
        <Button type="submit" variant="primary" disabled={!valid || !dirty} className="mt-[23px]">
          Save
        </Button>
      </form>
    </Section>
  );
}

/** Formats a rate compactly: up to 6 significant digits, no trailing zeros. */
function formatRate(v: number) {
  return Number(v.toPrecision(6)).toLocaleString(undefined, { maximumFractionDigits: 8, useGrouping: false });
}

/** One side of a rate: "1 FROM = [value] TO". Saves on blur or Enter. */
function RateField({ from, to, value, onSave }: { from: string; to: string; value: number; onSave: (v: number) => void }) {
  const [draft, setDraft] = useState(formatRate(value));
  const [focused, setFocused] = useState(false);
  const shown = focused ? draft : formatRate(value);
  const commit = () => {
    setFocused(false);
    const v = Number(draft.replace(/,/g, ""));
    if (Number.isFinite(v) && v > 0 && formatRate(v) !== formatRate(value)) onSave(v);
  };
  return (
    <label className="flex items-center gap-1.5 text-sm whitespace-nowrap text-muted" dir="ltr">
      1 {from} =
      <input
        value={shown}
        inputMode="decimal"
        aria-label={`${to} per 1 ${from}`}
        onFocus={() => {
          setDraft(formatRate(value));
          setFocused(true);
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
        className="h-8 w-28 rounded-md border border-line bg-transparent px-2 text-end text-sm text-fg tabular-nums outline-none focus:border-accent"
      />
      {to}
    </label>
  );
}

function CurrencyRow({ fin, currency: c }: { fin: Finance; currency: FinanceCurrency }) {
  const { pid } = useProject();
  const run = useFinanceRun();
  const { confirm } = useFeedback();
  const main = fin.settings.currency;
  const save = (rate: number) => run(api.updateCurrency(pid, c.id, { rate }));
  return (
    <>
      <span className={cn("w-12 font-mono text-sm font-medium", !c.visible && "text-faint")}>{c.code}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-1">
        <RateField from={main} to={c.code} value={c.rate} onSave={save} />
        <RateField from={c.code} to={main} value={1 / c.rate} onSave={(v) => save(1 / v)} />
      </div>
      <button
        type="button"
        onClick={() => run(api.updateCurrency(pid, c.id, { visible: !c.visible }))}
        className={cn("flex h-7 items-center gap-1 rounded-md px-2 text-[13px] hover:bg-hover", c.visible ? "text-fg" : "text-faint")}
        aria-pressed={c.visible}
        title={c.visible ? "Hide its equivalents" : "Show its equivalents"}
      >
        {c.visible ? <EyeIcon className="size-4" /> : <EyeSlashIcon className="size-4" />}
        {c.visible ? "Shown" : "Hidden"}
      </button>
      <button
        type="button"
        className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
        title="Remove currency"
        onClick={async () => {
          if (await confirm({ title: `Remove ${c.code}?`, message: "Its equivalents will no longer be shown. Recorded amounts don't change.", confirmLabel: "Remove", danger: true }))
            run(api.deleteCurrency(pid, c.id));
        }}
      >
        <TrashIcon className="size-4" />
      </button>
    </>
  );
}

function CurrenciesSettings({ fin }: { fin: Finance }) {
  const { pid } = useProject();
  const run = useFinanceRun();
  const [code, setCode] = useState("");
  const [rate, setRate] = useState("");
  const main = fin.settings.currency;
  const rateValue = Number(rate.replace(/,/g, ""));
  const validCode = /^[A-Z]{3}$/.test(code) && code !== main && !fin.currencies.some((c) => c.code === code);
  const valid = validCode && Number.isFinite(rateValue) && rateValue > 0;
  const pairs = fin.currencies.flatMap((a, i) => fin.currencies.slice(i + 1).map((b) => ({ a, b })));

  return (
    <Section
      title="Other currencies"
      description={`Show what amounts are worth in other currencies. Their equivalents appear in dimmer text next to or below every ${main} amount. Nothing is recorded in them, and changing a rate only changes what's displayed.`}
    >
      {fin.currencies.length > 0 && (
        <ReorderList items={fin.currencies} onReorder={(ids) => run(api.reorderCurrencies(pid, ids))} render={(c) => <CurrencyRow fin={fin} currency={c} />} />
      )}
      <form
        className="flex flex-wrap items-center gap-2 ps-7"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!valid) return;
          if (await run(api.createCurrency(pid, { code, rate: rateValue }))) {
            setCode("");
            setRate("");
          }
        }}
      >
        <input
          value={code}
          maxLength={3}
          onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, ""))}
          placeholder="Code"
          aria-label="Currency code"
          className="field h-8 w-20 py-0 font-mono uppercase"
          dir="ltr"
        />
        <label className="flex items-center gap-1.5 text-sm text-muted" dir="ltr">
          1 {main} =
          <input
            value={rate}
            inputMode="decimal"
            onChange={(e) => setRate(e.target.value)}
            placeholder="rate"
            aria-label="Exchange rate"
            className="field h-8 w-28 py-0 text-end tabular-nums"
          />
          {code || "…"}
        </label>
        <Button type="submit" size="sm" icon={PlusIcon} disabled={!valid}>
          Add currency
        </Button>
        {code.length === 3 && !validCode && <span className="text-xs text-danger">{code === main ? "That's the main currency" : "Already added"}</span>}
      </form>
      {pairs.length > 0 && (
        <div className="ps-7">
          <div className="mb-1 text-[13px] font-medium text-muted">Rates between them</div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted tabular-nums" dir="ltr">
            {pairs.map(({ a, b }) => (
              <span key={a.id + b.id}>
                1 {a.code} = {formatRate(b.rate / a.rate)} {b.code}
              </span>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

function LevelsSettings({ fin }: { fin: Finance }) {
  const { pid } = useProject();
  const run = useFinanceRun();
  const { confirm } = useFeedback();
  return (
    <Section
      title="Experience levels"
      description="Each member's level multiplies the credit they get for finished work. Assign levels on the Team pay tab; members without a level count as ×1."
    >
      <ReorderList
        items={fin.levels}
        onReorder={(ids) => run(api.reorderLevels(pid, ids))}
        render={(l) => (
          <>
            <ColorDot color={l.color} onChange={(color) => run(api.updateLevel(pid, l.id, { color }))} />
            <InlineName value={l.name} onSave={(name) => run(api.updateLevel(pid, l.id, { name }))} />
            <MultiplierInput value={l.multiplier} label={`${l.name} multiplier`} onSave={(multiplier) => run(api.updateLevel(pid, l.id, { multiplier }))} />
            <button
              type="button"
              className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
              title="Delete level"
              onClick={async () => {
                const ok = await confirm({
                  title: `Delete "${l.name}"?`,
                  message: "Members with this level will count as ×1 from now on. Payments already received keep their split.",
                  confirmLabel: "Delete",
                  danger: true,
                });
                if (ok) run(api.deleteLevel(pid, l.id));
              }}
            >
              <TrashIcon className="size-4" />
            </button>
          </>
        )}
      />
      <AddRow placeholder="Add a level" onAdd={(name) => run(api.createLevel(pid, { name, color: randomColor(), multiplier: 1 }))} />
    </Section>
  );
}

function WeightCard({ fin, which }: { fin: Finance; which: "scope" | "complexity" }) {
  const { pid, propsFor } = useProject();
  const run = useFinanceRun();
  const field = which === "scope" ? "scopePropertyId" : "complexityPropertyId";
  const selected = fin.settings[field];
  const candidates = propsFor("task").filter((p) => p.type !== "date");
  const property = candidates.find((p) => p.id === selected);

  return (
    <div className="min-w-0 flex-1 rounded-lg border border-line p-3">
      <div className="mb-1 font-medium">{which === "scope" ? "Scope" : "Complexity"}</div>
      <p className="mb-3 text-xs text-muted">
        {which === "scope"
          ? "A task's scope weights are added up, so a Backend + Frontend task counts for both."
          : "The task's complexity weight multiplies its scope total."}{" "}
        Tasks with no value count as ×1.
      </p>
      <select
        value={selected ?? ""}
        onChange={(e) => run(api.updateFinance(pid, { [field]: e.target.value || null }))}
        className="field mb-3"
        aria-label={`${which} property`}
      >
        <option value="">Not used</option>
        {candidates.map((p) => (
          <option key={p.id} value={p.id}>
            Task property: {p.name}
          </option>
        ))}
      </select>
      {property && (
        <div className="space-y-1">
          {property.options.map((o) => (
            <div key={o.id} className="flex items-center justify-between gap-2">
              <Tag color={o.color}>{o.label}</Tag>
              <MultiplierInput value={fin.weights[o.id] ?? 1} label={`${o.label} weight`} onSave={(w) => run(api.setOptionWeight(pid, o.id, w))} />
            </div>
          ))}
          {property.options.length === 0 && <p className="text-sm text-muted">This property has no options yet.</p>}
        </div>
      )}
    </div>
  );
}

export function RulesTab({ fin }: { fin: Finance }) {
  return (
    <div className="space-y-12">
      <ContractSettings fin={fin} />
      <CurrenciesSettings fin={fin} />
      <Section
        title="Task weights"
        description="Only tasks in a Done stage count. Task weight = complexity × sum of scope weights. It's split equally between the task's assignees, and each part is multiplied by that person's experience level."
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <WeightCard fin={fin} which="scope" />
          <WeightCard fin={fin} which="complexity" />
        </div>
      </Section>
      <LevelsSettings fin={fin} />
    </div>
  );
}
